import { useState, useEffect, forwardRef } from "react";

const LazyLoadImage = forwardRef(
  ({ src, alt, placeholder, fallback, width, height }, ref) => {
    const [isInView, setIsInView] = useState(false);
    const [imageSrc, setImageSrc] = useState(placeholder || fallback || src);
    const [isLoaded, setIsLoaded] = useState(false);

    useEffect(() => {
      if (!ref.current) return;

      const observer = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      });

      observer.observe(ref.current);

      return () => {
        observer.disconnect();
      };
    }, [ref]);

    useEffect(() => {
      if (isInView) {
        let isMounted = true;
        
        // Immediately use fallback for Facebook CDN URLs that might give 403
        if (src?.includes('fbcdn.net') || src?.includes('facebook')) {
          setImageSrc(fallback || placeholder || '');
          setIsLoaded(true);
          return;
        }

        const loadImage = async () => {
          try {
            const img = new Image();
            
            const loadPromise = new Promise((resolve, reject) => {
              img.onload = resolve;
              img.onerror = reject;
            });

            img.src = src;
            await loadPromise;

            if (isMounted) {
              setImageSrc(src);
              setIsLoaded(true);
            }
          } catch (error) {
            if (isMounted) {
              // Silently fall back without console error
              setImageSrc(fallback || placeholder || '');
              setIsLoaded(true);
            }
          }
        };

        loadImage();

        return () => {
          isMounted = false;
        };
      }
    }, [isInView, src, fallback, placeholder]);

    return (
      <div style={{ width, height, position: "relative", overflow: "hidden" }}>
        <img
          ref={ref}
          src={imageSrc}
          alt={alt}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
            position: "absolute",
            top: 0,
            left: 0,
            opacity: isLoaded ? 1 : 0,
            transform: isLoaded ? "scale(1)" : "scale(1.1)",
            transition: "opacity 0.5s ease-in-out, transform 0.5s ease-in-out",
          }}
        />
        {!isLoaded && (
          <div
            style={{
              width: "100%",
              height: "100%",
              background: "transparent",
              opacity: 0.5,
              animation: "fade-in 0.5s ease-in-out",
            }}
          />
        )}
      </div>
    );
  }
);

LazyLoadImage.displayName = "LazyLoadImage";

export default LazyLoadImage;
